/**
 * A chord already played.
 *
 * Compact and muted: history is context, not content. It exists so the player can see the
 * shape of what they have been doing at a glance, which is why the chord symbol stays
 * above the legibility floor even here — a history you have to lean in to read is a
 * history nobody reads.
 *
 * With `onOpen` it is also how you go back — but not in one tap any more. It used to
 * rewind on the spot, which meant a target the size of a chord symbol silently discarded
 * every chord after it with no preview and no warning. Now it opens that chord's detail:
 * what it was, when, how it was arrived at, and the rewind as a named action with the cost
 * written down beside it. The rewind itself is unchanged — same `stepBackTo`, same
 * `STEP_BACK`, still no redo. Without the handler the node is inert, exactly as before.
 */

import { motion } from 'framer-motion';
import type { SessionNode } from '@hc/shared';
import { GhostStubs, type GhostStub } from './GhostStubs.js';
import { PathConnector } from './PathConnector.js';
import { ChordSymbol } from '../system/ChordSymbol.js';
import { nodeLayoutId, nodeTransition } from './motion.js';

export interface HistoryNodeProps {
  node: SessionNode;
  symbol: string;
  /** Options offered at this node that the player did not take. Often empty: the reducer
   *  prunes old branch sets, and a history with no ghosts is not a bug. */
  ghosts: readonly GhostStub[];
  /** How far back this node is. Older nodes recede. */
  distance: number;
  calm: boolean;
  /** The connector leading *into* this node, tinted by how adventurous that move was. */
  incomingAdventurousness: number;
  /** False for the first node in the path, which has nothing to its left. */
  showConnector: boolean;
  /** Present when backtracking is wired. Absent leaves the node inert. */
  onOpen?: (nodeId: string) => void;
}

/** Older history fades, but never below the point where the symbol is still readable. */
function recede(distance: number): number {
  return Math.max(0.34, 0.78 - distance * 0.11);
}

export function HistoryNode({
  node,
  symbol,
  ghosts,
  distance,
  calm,
  incomingAdventurousness,
  showConnector,
  onOpen,
}: HistoryNodeProps): React.JSX.Element {
  return (
    <li className="hc-rail__item hc-rail__item--history">
      {showConnector ? <PathConnector adventurousness={incomingAdventurousness} /> : null}
      <motion.div
        className="hc-history"
        data-testid="hc-history-node"
        data-node-id={node.id}
        data-source={node.source}
        layoutId={calm ? undefined : nodeLayoutId(node)}
        initial={false}
        animate={{ opacity: recede(distance) }}
        transition={nodeTransition(calm)}
      >
        {onOpen ? (
          <button
            type="button"
            className="hc-history__symbol hc-history__open"
            data-testid="hc-history-open"
            data-node-id={node.id}
            onClick={() => onOpen(node.id)}
            // "Look at", not "go back to": the tap no longer commits to anything, and a
            // label promising a rewind would be a label promising the old behaviour.
            aria-label={`Look at ${symbol}`}
          >
            <ChordSymbol symbol={symbol} />
          </button>
        ) : (
          <span className="hc-history__symbol">
            <ChordSymbol symbol={symbol} />
          </span>
        )}
        <GhostStubs stubs={ghosts} />
      </motion.div>
    </li>
  );
}
